import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";

// ─────────────────────────────────────────────────────────────────────────────
// `config/appLayout` — the remote layout config the mobile app reads live.
//
// The app ships the demos; this doc decides which of them appear, in what
// order, and under what copy. It is world-readable (guests browse the
// Playground before signing in) and admin-writable — see firestore.rules.
//
// The doc can only ever hide / reorder / relabel a card the binary already
// ships. It cannot introduce one, so it can never point the app at a screen
// that doesn't exist. The app re-validates this on its side too.
// ─────────────────────────────────────────────────────────────────────────────

export const APP_LAYOUT_PATH = ["config", "appLayout"];

/** Must stay in sync with fw_mobile/src/features/playground/cards.ts. */
export const PLAYGROUND_CARDS = [
  {
    id: "Estimator",
    title: "Project estimator",
    subtitle: "Scope your build, see a live budget",
    color: "#C4271D",
    themed: true,
    blurb: "Interactive scope + live budget calculator.",
  },
  {
    id: "Shop",
    title: "E-commerce",
    subtitle: "Product → cart → checkout",
    color: "#C4271D",
    themed: true,
    blurb: "The real, admin-managed merch store.",
  },
  {
    id: "Rewards",
    title: "Rewards",
    subtitle: "Points, tiers, unlock animation",
    color: "#2d3a2e",
    blurb: "Simulated points, tiers and celebration animation.",
  },
  {
    id: "Subscriptions",
    title: "Dev retainer",
    subtitle: "Hire bboy · 6-month term",
    color: "#3a2d4f",
    blurb: "Retainer plans pulled from the live Stripe catalog.",
  },
  {
    id: "Notifications",
    title: "Push notifications",
    subtitle: "Real FCM pushes, sent live to this device",
    color: "#1f2f3e",
    blurb: "Sends a real FCM push to the current device.",
  },
  {
    id: "Rbac",
    title: "Role-based access",
    subtitle: "Allow/deny permission matrix",
    color: "#2d3a2e",
    blurb: "Internal permissions matrix. Hidden by default.",
  },
  {
    id: "Location",
    title: "Location services",
    subtitle: "Detect your city to tailor pricing",
    color: "#3a2f1f",
    blurb: "Reverse-geocodes the device to localise pricing.",
  },
];

export const CARD_IDS = PLAYGROUND_CARDS.map((c) => c.id);

/** What the app assumes when the doc is missing or unreachable. */
export const DEFAULT_LAYOUT = {
  playground: {
    Rbac: { visible: false },
  },
};

const ref = () => doc(db, ...APP_LAYOUT_PATH);

const known = new Set(CARD_IDS);

const clean = (raw) => {
  const playground = {};
  const src = (raw && raw.playground) || {};
  for (const [id, v] of Object.entries(src)) {
    if (!known.has(id) || !v || typeof v !== "object") continue;
    const entry = {};
    if (typeof v.visible === "boolean") entry.visible = v.visible;
    if (typeof v.order === "number" && Number.isFinite(v.order)) entry.order = v.order;
    if (v.title) entry.title = String(v.title).trim();
    if (v.subtitle) entry.subtitle = String(v.subtitle).trim();
    if (v.color) entry.color = String(v.color).trim();
    playground[id] = entry;
  }
  // Merge the app's defaults UNDERNEATH whatever the server actually stored,
  // exactly as fw_mobile/src/lib/appLayout.ts does. Without this the dashboard
  // renders a never-written doc as "Role-based access is visible" while the app
  // hides it — the preview would be lying about what users see.
  return { playground: { ...DEFAULT_LAYOUT.playground, ...playground } };
};

/** Live subscription. Falls back to defaults if the doc doesn't exist yet. */
export const subscribeAppLayout = (cb, onError) =>
  onSnapshot(
    ref(),
    (snap) => cb(snap.exists() ? clean(snap.data()) : DEFAULT_LAYOUT),
    (err) => {
      console.warn("[appLayout] snapshot error", err);
      onError?.(err);
    },
  );

/**
 * Replace the whole doc. Deliberately NOT a merge write: a merge can only ever
 * add keys to the nested `playground` map, so clearing an override would
 * silently leave the old value in place.
 */
export const saveAppLayout = (layout) =>
  setDoc(ref(), { ...clean(layout), updatedAt: serverTimestamp() });

/** The order the app will actually render, given a layout. */
export const resolveOrder = (layout) =>
  PLAYGROUND_CARDS.map((card, index) => ({
    card,
    index,
    entry: layout?.playground?.[card.id] || {},
  }))
    .map((row) => ({ ...row, sort: row.entry.order ?? row.index }))
    .sort((a, b) => a.sort - b.sort || a.index - b.index);
